'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ContactFormData } from '@workspace/interfaces/contact-form.types';
import { contactFormSchema } from '@workspace/schema/contact-form.schema';
import type { IFormSubmissionResult } from '../interfaces';
import { createHubSpotSubmissionData } from '@/app/lib/hubspot-utils';
import { GENERIC_HUBSPOT_ERROR_MESSAGE } from '@workspace/constants/contact-form.constant';
import { useHubSpot } from '@workspace/hooks/useHubSpot';

export function useContactForm() {
  const [result, setResult] = useState<IFormSubmissionResult | null>(null);
  const [messageLength, setMessageLength] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const { submitToHubSpot, loading } = useHubSpot();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      company: '',
      state: '',
      area_of_work: '',
      where_did_you_hear_about_porta_: '',
      enquiry_type___dropdown: '',
      message: '',
      newsletter: false,
      honeypot: '',
    },
  });

  const handleMessageChange = (value: string): string => {
    setMessageLength(value.length);
    return value;
  };

  const onSubmit = async (values: ContactFormData): Promise<void> => {
    setResult(null);

    if (values.honeypot && values.honeypot.trim() !== '') {
      return;
    }

    try {
      const submissionData = createHubSpotSubmissionData(values);
      const response = await submitToHubSpot(submissionData);

      if (response.success) {
        setResult({
          inlineMessage: 'Thank you for your enquiry.\nOne of our team member will be in contact with you soon.',
        });

        // Hide form on successful submission
        setShowForm(false);
      } else {
        setResult({
          error: true,
          inlineMessage: GENERIC_HUBSPOT_ERROR_MESSAGE,
        });
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setResult({
        error: true,
        inlineMessage: GENERIC_HUBSPOT_ERROR_MESSAGE,
      });
    }
  };

  return {
    form,
    loading,
    result,
    messageLength,
    showForm,
    handleMessageChange,
    onSubmit,
  };
}
